import { useQuery } from '@apollo/client';
import Alert from '@mui/material/Alert';
import { RHFAutoCompleteVirtualize } from '@openx/components-core/lib/AutoComplete/RHFAutoCompleteVirtualize.tsx';
import { FormSection } from '@openx/components-core/lib/FormSection/FormSection.tsx';
import { RIGHT_COLUMN_WIDTH } from '@openx/constants/form.ts';
import { Order_By } from '@openx/types/gqlTypes.ts';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo } from 'react';
import { type Path, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { type Location, useLocation } from 'react-router';
import { GetOrganizationsListDocument } from '../../../../graphql/query/organizations/GetOrganizationsList.generated.tsx';
import type { AudienceLocationState } from '../AudienceFormFields.tsx';
import type { AudienceForm } from '../types.ts';
import { getInitialValues } from '../utils/getInitialValues.ts';
import { mapOrganizationsToAutocompleteOptions } from '../utils/mapOrganizationsToAutocompleteOptions.ts';

type OnBehalfOfSectionProps = {
  isEdit?: boolean;
};

export const OnBehalfOfSection = ({ isEdit }: OnBehalfOfSectionProps) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const location: Location<AudienceLocationState> = useLocation();
  const {
    formState: { isSubmitting },
    reset,
  } = useFormContext<AudienceForm>();
  const {
    data: { account: organizations = [] } = {},
    loading: organizationsLoading,
    error: organizationsError,
  } = useQuery(GetOrganizationsListDocument, {
    variables: {
      order_by: [{ name: Order_By.Asc }],
    },
  });

  const options = useMemo(() => mapOrganizationsToAutocompleteOptions(organizations), [organizations]);

  useEffect(() => {
    if (organizationsError) {
      enqueueSnackbar(t('Could not get organizations data from the server'), { variant: 'error' });
    }
  }, [organizationsError, enqueueSnackbar, t]);

  return (
    <FormSection divider title={t('Work on Behalf')} rightColumnSize={RIGHT_COLUMN_WIDTH}>
      <RHFAutoCompleteVirtualize
        name={'account_id' satisfies Path<AudienceForm>}
        options={options.values}
        getOptionLabel={option => options.labels[option] || ''}
        textFieldProps={{
          label: t('Organization'),
          placeholder: t('[ Select Organization ]'),
          margin: 'dense',
        }}
        loading={organizationsLoading}
        onChange={(_, value) => {
          reset(getInitialValues(value));
        }}
        data-test="organization-name"
        disabled={isSubmitting || !!location.state?.accountId || isEdit}
        disableClearable
      />

      {!isEdit && !location.state?.accountId && (
        <Alert severity="info" data-test="work-on-behalf-alert-message" sx={{ mt: 1 }}>
          {t('Changing the Organization will remove all audience options, details and criteria inputs.')}
        </Alert>
      )}
    </FormSection>
  );
};
